/**
 * Render resolution: a fixed fraction of the display's, or chosen on the fly.
 *
 * The scene is drawn at `devicePixelRatio × scale` (the ratio capped at 2) and
 * the browser stretches it to the window. Fill rate — shading, occlusion, the
 * post passes — goes with the square of the scale, so 75 % costs about 56 % of
 * the pixels and 50 % a quarter, while the geometry, the draw distance and
 * everything streamed stay exactly as they are.
 *
 * `auto` is dynamic resolution: it steps down while the frame rate stays low
 * and back up once it recovers, with enough hysteresis that a hitch while a
 * tile streams in does not flip it. The choice is remembered per browser, and
 * `?scale=0.75` or `?scale=auto` overrides it for one visit.
 */

export type ScaleSetting = 'auto' | number;

export const SCALE_CHOICES: ScaleSetting[] = ['auto', 0.5, 0.75, 1];

const STORAGE_KEY = 'wsg.renderScale';
const MIN_AUTO = 0.5;
const STEP = 0.125;
/** Frame rate below which auto steps down, and at or above which it may step up. */
const LOW_FPS = 48;
const HIGH_FPS = 56;
/** One-second windows in a row needed before stepping down, and up. */
const WINDOWS_DOWN = 2;
const WINDOWS_UP = 4;

function parse(value: string | null): ScaleSetting | undefined {
  if (value === null) return undefined;
  if (value === 'auto') return 'auto';
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.max(0.25, Math.min(1, n)) : undefined;
}

function load(search: string): ScaleSetting {
  const fromUrl = parse(new URLSearchParams(search).get('scale'));
  if (fromUrl !== undefined) return fromUrl;
  try {
    return parse(localStorage.getItem(STORAGE_KEY)) ?? 'auto';
  } catch {
    return 'auto';
  }
}

/** After stepping down, the least wait before trying a step back up, ms. */
const UP_COOLDOWN = 15_000;
/** The longest that wait grows to when steps up keep being undone, ms. */
const MAX_UP_COOLDOWN = 120_000;
/** A step down this soon after a step up means the step up was a mistake. */
const REGRET = 20_000;

export class RenderScale {
  setting: ScaleSetting;
  /** The scale in use right now; under `auto`, where the controller has got to. */
  scale: number;
  private frameTimes: number[] = [];
  private lastFrame = 0;
  private windowStart = performance.now();
  private lowWindows = 0;
  private highWindows = 0;
  /**
   * A new pixel ratio waiting for the start of the next frame. Resizing the
   * canvas clears it, so a resize applied after a frame has rendered shows the
   * browser one cleared — black — frame. Applied before rendering instead, it
   * is drawn over at once and never seen.
   */
  private pending: number | undefined;
  private lastUp = -Infinity;
  private lastDown = -Infinity;
  private upCooldown = UP_COOLDOWN;

  constructor(
    search: string,
    /** Applies a device pixel ratio to the renderer and everything sized by it. */
    private readonly apply: (pixelRatio: number) => void,
    /** Most device pixels a CSS pixel: 2 on a desktop, 1.5 on a phone (see `Graphics`). */
    private maxRatio = 2,
  ) {
    this.setting = load(search);
    this.scale = this.setting === 'auto' ? 1 : this.setting;
    this.apply(this.base * this.scale);
  }

  /** The window's device pixel ratio, capped: beyond 2 nothing is visibly sharper, and on a phone's dense screen beyond 1.5. */
  private get base(): number {
    return Math.min(window.devicePixelRatio, this.maxRatio);
  }

  /** A new cap on device pixels a CSS pixel, applied before the next frame. */
  setMaxRatio(ratio: number): void {
    this.maxRatio = ratio;
    this.commit();
  }

  choose(setting: ScaleSetting): void {
    this.setting = setting;
    try {
      localStorage.setItem(STORAGE_KEY, String(setting));
    } catch {
      // Not remembered, but still applied.
    }
    this.scale = setting === 'auto' ? 1 : setting;
    this.upCooldown = UP_COOLDOWN;
    this.resetWindows();
    this.commit();
  }

  /**
   * Re-apply after a resize or a change of screen, which can change the pixel
   * ratio. The window has already resized the canvas, so this applies at once.
   */
  refresh(): void {
    this.pending = undefined;
    this.apply(this.base * this.scale);
  }

  /** Call at the very start of a frame, before anything is drawn. */
  beforeRender(): void {
    if (this.pending === undefined) return;
    const ratio = this.pending;
    this.pending = undefined;
    this.apply(ratio);
  }

  /**
   * Count a frame. `measuring` is false while nothing is being drawn — the
   * world map is open, or the tab is hidden — so those frames say nothing.
   *
   * The frame rate of a window is its *median* frame time, not its average:
   * a tile being rebuilt is one long frame, and an average counts it as a
   * slow second. That hitch is not what resolution can fix, and reacting to
   * it is what made the scale step down, then back up, again and again.
   */
  frame(now: number, measuring: boolean): void {
    if (this.setting !== 'auto') return;
    const dt = now - this.lastFrame;
    this.lastFrame = now;
    if (!measuring) {
      this.resetWindows(now);
      return;
    }
    if (dt > 0 && dt < 1000) this.frameTimes.push(dt);
    const elapsed = now - this.windowStart;
    if (elapsed < 1000) return;
    const times = this.frameTimes.sort((a, b) => a - b);
    const median = times.length > 0 ? times[Math.floor(times.length / 2)] : Infinity;
    this.frameTimes = [];
    this.windowStart = now;
    // A window that long is a stall — a background tab, a debugger — not a
    // frame rate.
    if (elapsed > 3000 || times.length < 5) return;
    const fps = 1000 / median;

    if (fps < LOW_FPS) {
      this.highWindows = 0;
      if (++this.lowWindows >= WINDOWS_DOWN && this.scale > MIN_AUTO) {
        // Down again soon after going up: that step up did not hold, so wait
        // twice as long before the next one.
        if (now - this.lastUp < REGRET) this.upCooldown = Math.min(MAX_UP_COOLDOWN, this.upCooldown * 2);
        this.scale = Math.max(MIN_AUTO, this.scale - STEP);
        this.lowWindows = 0;
        this.lastDown = now;
        this.commit();
      }
    } else if (fps >= HIGH_FPS) {
      this.lowWindows = 0;
      if (++this.highWindows >= WINDOWS_UP && this.scale < 1 && now - this.lastDown >= this.upCooldown) {
        this.scale = Math.min(1, this.scale + STEP);
        this.highWindows = 0;
        this.lastUp = now;
        this.commit();
      }
    } else {
      this.lowWindows = 0;
      this.highWindows = 0;
    }
  }

  private resetWindows(now = performance.now()): void {
    this.frameTimes = [];
    this.windowStart = now;
    this.lastFrame = now;
    this.lowWindows = 0;
    this.highWindows = 0;
  }

  /** Ask for the current scale, to be applied before the next frame renders. */
  private commit(): void {
    this.pending = this.base * this.scale;
  }
}
